import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { PurchaseStatus } from "@/app/payments/types";
import { BannersPlacement } from "@/components/BannersPlacement/BannersPlacement";
import { BookingError } from "@/components/Errors/BookingError";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { MoreDetailsDrawer } from "@/components/MoreDetailsDrawer/MoreDetailsDrawer";
import { OTPPopup } from "@/components/OTPPopup/OTPPopup";
import { PriceChangedModal } from "@/components/PriceChangedModal/PriceChangedModal";
import { PromoCodeForm } from "@/components/PromoCodeForm/PromoCodeForm";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { usePurchaseStateContext } from "@/contexts/PurchaseStateContext";
import BottomDrawer from "@/features/flights/components/flight-purchase/BottomDrawer";
import { FlightOfferError } from "@/features/flights/components/flight-purchase/FlightOfferError";
import FlightSummary from "@/features/flights/components/flight-purchase/FlightSummary";
import InstallmentDetails from "@/features/flights/components/flight-purchase/InstallmentDetails";
import { PaymentDetails } from "@/features/flights/components/flight-purchase/PaymentDetails";
import Summary from "@/features/flights/components/flight-purchase/Summary";
import { TravellersListSummary } from "@/features/flights/components/flight-purchase/TravellersListSummary";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import {
  flightAvailabilityQueryOptions,
  useFlightAvailabilityMutation,
} from "@/features/flights/hooks/useFlightAvailability";
import { useFlightBooking } from "@/features/flights/hooks/useFlightBooking";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { transformFlightTraveler } from "@/features/travellers/utils/transformTraveler";
import { usePromoCodeValidation } from "@/hooks/usePromoCodeValidation";
import { FlightTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import type { GetFlightAvailabilityTokenOutputDto } from "@/types/codegen";
import { BannersPlacementSlug } from "@/types/enums";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute(
  "/_authenticated/flights/_flights/_flight-offer/offer/$id/step/3",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();

  const { paymentType, sessionId, selectedInstallments, refreshSession } =
    usePaymentContext();
  const { updatePurchaseState, purchaseState } = usePurchaseStateContext();
  const { selectedFlightPayload, promoCode, setPromoCode } =
    useFlightSelectionPayloadContext();
  const { data: singleFlightDetails, isLoading: isAdditionalDataLoading } =
    useQuery(flightAvailabilityQueryOptions(selectedFlightPayload));
  const { selectedTravellers } = useTravellersSelectionContext();

  const [termsAndConditions, setTermsAndConditions] = useState(false);
  const [moreDetailsOpen, setMoreDetailsOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showOTP, setShowOTP] = useState(false);

  const arePromoCodesEnabled = useFeatureIsOn("promo-codes");

  const {
    data: promoCodeData,
    validationStatus: promoCodeValidationStatus,
    revalidate,
  } = usePromoCodeValidation({
    service: "flights",
    validationRequestData: {
      promoCode: promoCode || "",
      availabilityToken: singleFlightDetails?.availabilityToken || "",
    },
  });

  const originalPrice = singleFlightDetails?.totalPrice || 0;
  const includedBnplFee = includeBnplFee(originalPrice);
  const totalPrice = paymentType === "bnpl" ? includedBnplFee : originalPrice;
  const totalPriceWithPromoCode = promoCodeData?.paymentAmount || totalPrice;
  const paymentMethod = paymentType === "bnpl" ? "bnpl" : "qiAccount";

  const { qiPayment, checkoutPayment, bnplPayment } = useFlightBooking({
    method: paymentMethod,
    initialAvailabilityToken: singleFlightDetails?.availabilityToken,
    onSuccess: ({ orderId, status }) => {
      if (status === PurchaseStatus.SUCCESSFUL) {
        if (paymentType === "bnpl") {
          // to update eligibility info
          refreshSession();
        }

        navigate({
          to: "/flights/offer/$id/step/final",
          search: { orderId },
        }).then(() => {
          updatePurchaseState("completed");
        });
      }
    },
  });

  const handleRefetchAvailabilitySuccess = (
    data: GetFlightAvailabilityTokenOutputDto,
  ) => {
    if (data.data) {
      const latestTotalPrice = data.data.totalPrice;
      if (latestTotalPrice !== originalPrice) {
        revalidate();
        setShowConfirmation(true);
      } else {
        handlePurchase(singleFlightDetails?.availabilityToken);
      }
    }
  };

  const {
    data: availability,
    isError: isAvailabilityError,
    error: availabilityError,
    isPending: isAvailabilityRefetching,
    mutate: refetchAvailability,
  } = useFlightAvailabilityMutation({
    onSuccess: handleRefetchAvailabilitySuccess,
  });

  const promoCodeToApply = promoCodeData?.result
    ? promoCode || undefined
    : undefined;

  const handlePurchase = (availabilityToken?: string) => {
    if (
      !selectedFlightPayload ||
      purchaseState === "completed" ||
      !availabilityToken
    ) {
      return;
    }

    rudderAnalytics.track(FlightTrackingEvents.USER_PROCEED_TO_PAYMENT_SCREEN, {
      amount: totalPriceWithPromoCode,
      payment_method: paymentMethod,
      currency: singleFlightDetails?.currency,
    });

    if (paymentMethod === "bnpl") {
      setShowOTP(true);
    } else if (paymentMethod === "qiAccount") {
      qiPayment.mutateAsync({
        promoCode: promoCodeToApply,
        offerId: selectedFlightPayload?.index,
        searchResultKey: selectedFlightPayload?.searchResultKey,
        searchResultToken: selectedFlightPayload?.searchResultToken,
        itineraryIndex: selectedFlightPayload?.itineraryIndex,
        availabilityToken,
        travelers: selectedTravellers.map((traveller) =>
          transformFlightTraveler(traveller),
        ),
      });
    } else {
      checkoutPayment.submit({
        promoCode: promoCodeToApply,
        offerId: selectedFlightPayload?.index,
        searchResultKey: selectedFlightPayload?.searchResultKey,
        searchResultToken: selectedFlightPayload?.searchResultToken,
        itineraryIndex: selectedFlightPayload?.itineraryIndex,
        availabilityToken,
        travelers: selectedTravellers.map((traveller) =>
          transformFlightTraveler(traveller),
        ),
      });
    }
  };

  const handleOTPSubmit = (otp: string) => {
    if (
      !selectedFlightPayload ||
      !singleFlightDetails?.availabilityToken ||
      !sessionId
    ) {
      return;
    }

    bnplPayment.mutateAsync({
      promoCode: promoCodeToApply,
      offerId: selectedFlightPayload.index,
      searchResultKey: selectedFlightPayload.searchResultKey,
      searchResultToken: selectedFlightPayload.searchResultToken,
      itineraryIndex: selectedFlightPayload.itineraryIndex,
      availabilityToken: singleFlightDetails.availabilityToken,
      travelers: selectedTravellers.map((traveller) =>
        transformFlightTraveler(traveller),
      ),
      sessionId,
      otp,
    });
  };

  const handleCancelPriceChange = () => {
    setShowConfirmation(false);
    navigate({ to: "/flights/search" });
  };

  if (isAvailabilityError)
    return <FlightOfferError error={availabilityError} />;

  if (qiPayment.isError) {
    return <BookingError error={qiPayment.error} type="flights" />;
  }

  if (
    qiPayment.isPending ||
    checkoutPayment.isPending ||
    bnplPayment.isPending
  ) {
    return (
      <div className="h-96">
        <PageLoader>
          <AnimatedLoader icon="airplane" />
          <h3 className="mt-3 font-normal text-text-primary">
            {intl.formatMessage({
              defaultMessage: "We are booking your flight...",
              description:
                "Loading message shown while the flight booking is being processed",
            })}
          </h3>
        </PageLoader>
      </div>
    );
  }

  return (
    <>
      <div className="px-4">
        <FlightSummary selectedFlight={singleFlightDetails} />

        {selectedTravellers.length > 0 && (
          <>
            <div className="section-title mt-4">
              {intl.formatMessage({
                defaultMessage: "Travellers List",
                description:
                  "Heading above the list of travellers assigned to the current booking",
              })}
            </div>
            <div className="section-card">
              <TravellersListSummary selectedTravellers={selectedTravellers} />
            </div>
          </>
        )}

        <BannersPlacement
          slug={BannersPlacementSlug.CHECKOUT}
          classNames={{
            wrapper: "-mx-4 mt-4",
          }}
        />

        {arePromoCodesEnabled ? (
          <div className="mt-4">
            <PromoCodeForm
              appliedCode={promoCode || ""}
              validationStatus={promoCodeValidationStatus}
              onCodeApply={({ code }) => {
                setPromoCode(code);
              }}
              onCodeRemove={() => {
                setPromoCode(null);
              }}
            />
          </div>
        ) : null}

        <Summary
          termsAndConditions={termsAndConditions}
          onTermsChange={setTermsAndConditions}
          onMoreDetails={() => {
            setMoreDetailsOpen(true);
          }}
        />
        {paymentMethod === "bnpl" ? (
          <InstallmentDetails
            promoCodeDiscount={promoCodeData?.discountAmount}
            totalPrice={totalPriceWithPromoCode}
          />
        ) : (
          <PaymentDetails
            promoCodeDiscount={promoCodeData?.discountAmount}
            totalPrice={totalPriceWithPromoCode}
            paymentMethod={paymentMethod}
          />
        )}

        <div id="flow-container" className="section-card p-0 not-empty:mt-2" />
      </div>

      <PriceChangedModal
        open={showConfirmation}
        price={formatCurrency(
          availability?.data.data.totalPrice,
          availability?.data.data.currency,
        )}
        onCancel={handleCancelPriceChange}
        onConfirm={() => {
          handlePurchase(availability?.data.data.availabilityToken);
        }}
      />

      <MoreDetailsDrawer
        open={moreDetailsOpen}
        onClose={() => setMoreDetailsOpen(false)}
      />

      <BottomDrawer
        totalPrice={
          paymentType === "bnpl"
            ? undefined
            : formatCurrency(totalPriceWithPromoCode)
        }
        payLaterPrice={
          paymentType === "bnpl"
            ? formatCurrency(
                calculateMonthlyInstallment(
                  includedBnplFee,
                  selectedInstallments,
                ),
              )
            : undefined
        }
        buttonLabel={intl.formatMessage({
          defaultMessage: "Purchase ticket",
          description:
            "Button to complete the purchase and issue the flight ticket",
        })}
        onButtonClick={() => {
          if (selectedFlightPayload) {
            refetchAvailability(selectedFlightPayload);
          }
        }}
        disabled={
          !termsAndConditions ||
          isAdditionalDataLoading ||
          promoCodeValidationStatus?.type === "loading"
        }
        loading={isAvailabilityRefetching}
      />

      <OTPPopup
        isOpen={showOTP}
        onClose={() => setShowOTP(false)}
        onSubmit={handleOTPSubmit}
        error={
          bnplPayment.error
            ? intl.formatMessage({
                defaultMessage: "OTP verification failed",
                description: "Error message shown when OTP verification fails",
              })
            : undefined
        }
      />
    </>
  );
}
